import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { FileUpload } from "primereact/fileupload";
import { Skeleton } from "primereact/skeleton";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { useRef, useState } from "react";
import { isValidSimpleUrl } from "../../utils/helper/isValidSimpleUrl";
import styles from "./styles.module.css";
import { Can, PERMISSIONS } from "../../security";

const YES_NO_OPTIONS = ["Yes", "No"];

const SAT_PROCTORING_OPTIONS = ["Yes", "No", "Proctored other exams"];

const EXPERIENCE_LEVEL_OPTIONS = [
  { label: "0 - 1 Year", value: "0 - 1 Year" },
  { label: "1 - 3 Years", value: "1 - 3 Years" },
  { label: "3 - 5 Years", value: "3 - 5 Years" },
  { label: "5 + Years", value: "5+ Years" }
];

const REMOTE_EQUIPMENT_OPTIONS = [
  { label: "USB Headset", value: "USB Headset" },
  { label: "1 Monitor", value: "1 Monitor" },
  { label: "2 Monitors", value: "2Monitors" },
  { label: "3 Monitors", value: "3 Monitors" },
  { label: "Quiet Space", value: "Quiet Space" },
  { label: "Backup Power", value: "Backup Power" },
  { label: "Backup Internet", value: "Backup Internet" },
];

function ApplicantJobDetails({ job, visible, onHide, onApply }) {
  const toast = useRef(null);

  const [openApplyDialog, setOpenApplyDialog] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [resumeBase64, setResumeBase64] = useState("");
  const [fileName, setFileName] = useState("");

  const [hasProctoredSatBefore, setHasProctoredSatBefore] = useState(null);
  const [availableForSatEvent, setAvailableForSatEvent] = useState(null);
  const [availableOnsiteByRequiredTime, setAvailableOnsiteByRequiredTime] = useState(null);
  const [acceptsUnresponsivePolicy, setAcceptsUnresponsivePolicy] = useState(null);
  const [hasLaptopAndMobileDevice, setHasLaptopAndMobileDevice] = useState(null);
  const [experienceLevel, setExperienceLevel] = useState(null);
  const [workingSpaceSetup, setWorkingSpaceSetup] = useState([]);

  const hasApplication = job?.applicationStatus;

  const isOnSiteJob = job?.location === "On-site";
  const isRemoteJob = job?.location === "Remote";

  const showError = (message) => {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  };

  const parseList = (value) => {
    if (!value) return [];
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const formatExamName = (exam) => {
    const labels = {
      TypingSpeedTest: "Typing Speed Test",
      CustomerServiceSimulation: "Customer Service Simulation",
      LogicAptitudeTest: "Logic / Aptitude Test",
      RoleKnowledgeTest: "Role-Specific Knowledge Test",
      TechnicalSkillsAssessment: "Technical Skills Assessment",
      CommunicationSkillsTest: "Communication Skills Test (Written/Verbal)",
      DataEntryAccuracyTest: "Data Entry Accuracy Test",
      Other: "Other",
    };

    return labels[exam] || exam;
  };

  const resetApplyForm = () => {
    setVideoUrl("");
    setResumeBase64("");
    setFileName("");

    setHasProctoredSatBefore(null);
    setAvailableForSatEvent(null);
    setAvailableOnsiteByRequiredTime(null);
    setAcceptsUnresponsivePolicy(null);
    setHasLaptopAndMobileDevice(null);

    setExperienceLevel(null);
    setWorkingSpaceSetup([]);
  };

  const handleFileSelect = (e) => {
    const file = e.files[0];
    if (!file) return;

    const allowedExtensions = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "ppt",
      "pptx",
      "txt",
    ];

    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      showError(
        "Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT"
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setResumeBase64(base64);
      setFileName(file.name);
    };

    reader.readAsDataURL(file);
  };

  const handleWorkingSpaceSetupChange = (e) => {
    if (e.checked) {
        setWorkingSpaceSetup((prev) => [...prev, e.value]);
        return;
    }

    setWorkingSpaceSetup((prev) =>
        prev.filter((item) => item !== e.value)
    );
  };

  const getInputId = (name, option) => {
    return `${name}-${option
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")}`;
  };

  const renderRadioOptions = (options, name, selectedValue, onChange) => {
    return (
      <div className="flex flex-column gap-3 mt-2">
        {options.map((option) => {
          const label =
            typeof option === "object" ? option.label : option;

          const value =
            typeof option === "object" ? option.value : option;

          const inputId = getInputId(name, String(value));

          return (
            <div
              key={value}
              className="flex align-items-center gap-2"
            >
              <RadioButton
                inputId={inputId}
                name={name}
                value={value}
                onChange={(e) => onChange(e.value)}
                checked={selectedValue === value}
              />

              <label htmlFor={inputId}>{label}</label>
            </div>
          );
        })}
      </div>
    );
  };

  const handleApply = async () => {
    try {
        setApplyLoading(true);

        if (!videoUrl) {
            showError("Video URL is required");
            return;
        }

        if (!isValidSimpleUrl(videoUrl)) {
            showError("Please provide a valid URL for your intro video");
            return;
        }

        if (!resumeBase64) {
            showError("Please upload your resume");
            return;
        }

        const applicationExtraFields = [];

        if (isOnSiteJob) {
            applicationExtraFields.hasProctoredSatBefore = hasProctoredSatBefore;
            applicationExtraFields.availableForSatEvent = availableForSatEvent;
            applicationExtraFields.availableOnsiteByRequiredTime = availableOnsiteByRequiredTime;
            applicationExtraFields.acceptsUnresponsivePolicy = acceptsUnresponsivePolicy;
            applicationExtraFields.hasLaptopAndMobileDevice = hasLaptopAndMobileDevice;
        }

        if (isRemoteJob) {
            applicationExtraFields.experienceLevel = experienceLevel;
            applicationExtraFields.workingSpaceSetup =
                workingSpaceSetup.length > 0
                    ? workingSpaceSetup.join(",")
                    : null;
        }

        await onApply(
            job.jobUuid || job.uuid,
            videoUrl,
            resumeBase64,
            fileName,
            applicationExtraFields
        );

        resetApplyForm();
        setOpenApplyDialog(false);
        onHide();

    } catch (error) {
        console.error(error);
        showError(
            "An error occurred while submitting your application. Please try again."
        );
    } finally {
        setApplyLoading(false);
    }
  };

  const handleCloseApplyDialog = () => {
    setOpenApplyDialog(false);
  };

  const DetailItem = ({ label, value }) => (
    <div className={styles.applicantDetailItem}>
      <span className={styles.applicantDetailLabel}>{label}</span>
      <p className={styles.applicantDetailValue}>{value || "Not informed"}</p>
    </div>
  );

  const DetailList = ({ label, items }) => (
    <div className={styles.applicantDetailItem}>
      <span className={styles.applicantDetailLabel}>{label}</span>

      {items.length > 0 ? (
        <div className={styles.applicantTags}>
          {items.map((item) => (
            <span key={item} className={styles.applicantTag}>
              {item}
            </span>
          ))}
        </div>
      ) : (
        <p className={styles.applicantDetailValue}>Not informed</p>
      )}
    </div>
  );

  const ApplicantSkeleton = () => (
    <div className={styles.applicantJobDetails}>
      <Skeleton width="50%" height="24px" className="mb-4" />
      <Skeleton height="90px" className="mb-3" />
      <Skeleton height="90px" className="mb-3" />
      <Skeleton height="90px" className="mb-3" />
    </div>
  );

  const exams = parseList(job?.assessments).map(formatExamName);
  const skills = parseList(job?.skills);
  const languages = parseList(job?.languages);

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Dialog
        visible={visible}
        header={job?.jobTitle || "Job Details"}
        onHide={onHide}
        dismissableMask
        className={styles.applicantJobDialog}
      >
        {!job ? (
          <ApplicantSkeleton />
        ) : (
          <div className={styles.applicantJobDetails}>
            <section className={styles.applicantSection}>
              <h4>Job Information</h4>

              <DetailItem label="Job Title" value={job.jobTitle} />
              <DetailItem label="Job Description" value={job.jobDescription} />
              <DetailItem label="Department" value={job.department} />
              <DetailItem label="Location" value={job.location} />
              <DetailItem label="Employment Type" value={job.employmentType} />
              <DetailItem label="Offer Rate" value={job.offerRate} />

              <DetailList label="Skills" items={skills} />
              <DetailList label="Languages" items={languages} />
            </section>

            <section className={styles.applicantSection}>
              <h4>Role Details</h4>

              <DetailItem label="Overview" value={job.overview} />
              <DetailItem
                label="Responsibilities"
                value={job.responsibilities}
              />
              <DetailItem label="Requirements" value={job.requirements} />
              <DetailItem
                label="Non-Negotiables"
                value={job.nonNegotiables}
              />
              <DetailItem label="Nice-To-Haves" value={job.niceToHaves} />
            </section>

            <section className={styles.applicantSection}>
              <h4>Benefits</h4>

              <DetailItem label="Benefits" value={job.benefits} />
            </section>

            <section className={styles.applicantSection}>
              <h4>Exams / Assessments</h4>

              <DetailList label="Required Assessments" items={exams} />

              {parseList(job.assessments).includes("Other") &&
                job.assessmentsOther && (
                  <DetailItem
                    label="Other Assessment"
                    value={job.assessmentsOther}
                  />
                )}
            </section>

            {!hasApplication && (
              <div className={styles.jobDetaisFooterBtns}>
                <Button
                  type="button"
                  label="Apply"
                  icon="pi pi-send"
                  severity="info"
                  onClick={() => setOpenApplyDialog(true)}
                />
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        visible={openApplyDialog}
        dismissableMask
        onHide={handleCloseApplyDialog}
        header="Application form"
        className="apply-dialog"
        footer={
          <div className="apply-dialog-footer">
            <Can permission={PERMISSIONS.jobApplicationsByUuidApplyByApplicantid_post}>
              <Button
                label="Apply"
                icon="pi pi-check"
                loading={applyLoading}
                onClick={handleApply}
              />
            </Can>

            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={handleCloseApplyDialog}
            />
          </div>
        }
      >
        <div className="apply-form">
          <div className="apply-field">
            <label htmlFor="job-details-apply-form-video-url">
              Introducing video URL
            </label>

            <InputText
              id="job-details-apply-form-video-url"
              placeholder="Paste your video link..."
              className="apply-input"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
          </div>

          <div className="apply-field">
            <label htmlFor="upload-resume">Resume</label>

            <small className="field-help">• Maximum size: 5 MB</small>

            <FileUpload
              mode="basic"
              name="upload-resume"
              id="upload-resume"
              url=""
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              chooseLabel="Upload resume"
              className="apply-upload"
              maxFileSize={5242880}
              invalidFileSizeMessageSummary="File is too large"
              invalidFileSizeMessageDetail="The maximum allowed size is 5 MB"
              onSelect={handleFileSelect}
            />
          </div>

          {isOnSiteJob && (
            <>
              <Divider />

              <div className="apply-field">
                <label>Have you proctored an SAT before?</label>

                {renderRadioOptions(
                  SAT_PROCTORING_OPTIONS,
                  "hasProctoredSatBefore",
                  hasProctoredSatBefore,
                  setHasProctoredSatBefore
                )}
              </div>

              <div className="apply-field">
                <label>
                  Are you available to join for the upcoming SAT event? Please
                  follow this link to find the dates:{" "}
                  <a
                    href="https://satsuite.collegeboard.org/sat/dates-deadlines"
                    target="_blank"
                    rel="noreferrer"
                  >
                    SAT dates and deadlines
                  </a>
                </label>

                {renderRadioOptions(
                  YES_NO_OPTIONS,
                  "availableForSatEvent",
                  availableForSatEvent,
                  setAvailableForSatEvent
                )}
              </div>

              <div className="apply-field">
                <label>
                  Will you be able to be onsite by no later than 10h15am Local
                  time, on the Friday, and 05h45am Local time, on the Saturday
                  to join the SAT event?
                </label>

                {renderRadioOptions(
                  YES_NO_OPTIONS,
                  "availableOnsiteByRequiredTime",
                  availableOnsiteByRequiredTime,
                  setAvailableOnsiteByRequiredTime
                )}
              </div>

              <div className="apply-field">
                <label>
                  If you become unresponsive, your spot may be given to another
                  candidate who is more responsive to our communications. Please
                  confirm below that you accept this.
                </label>

                {renderRadioOptions(
                  YES_NO_OPTIONS,
                  "acceptsUnresponsivePolicy",
                  acceptsUnresponsivePolicy,
                  setAcceptsUnresponsivePolicy
                )}
              </div>

              <div className="apply-field">
                <label>
                  Do you have a laptop and mobile device that you can use for
                  the event? (Note that you will need both)
                </label>

                {renderRadioOptions(
                  YES_NO_OPTIONS,
                  "hasLaptopAndMobileDevice",
                  hasLaptopAndMobileDevice,
                  setHasLaptopAndMobileDevice
                )}
              </div>
            </>
          )}

          {isRemoteJob && (
            <>
              <Divider />

              <div className="apply-field">
                <label>
                  Please select your experience level in the role you are
                  applying for (Linked to Zammad Ticket Field)
                </label>

                {renderRadioOptions(
                  EXPERIENCE_LEVEL_OPTIONS,
                  "experienceLevel",
                  experienceLevel,
                  setExperienceLevel
                )}
              </div>

              <div className="apply-field">
                <label>
                  As this role requires specific equipment and resources, please
                  select all the items you currently have available from the
                  list below. (Linked to Zammad Ticket Field)
                </label>

                <div className="flex flex-column gap-3 mt-2">
                  {REMOTE_EQUIPMENT_OPTIONS.map((equipment) => {
                    const inputId = getInputId("workingSpaceSetup", equipment.label);

                    return (
                        <div
                            key={equipment.value}
                            className="flex align-items-center gap-2"
                        >
                            <Checkbox
                                inputId={inputId}
                                name="workingSpaceSetup"
                                value={equipment.value}
                                checked={workingSpaceSetup.includes(equipment.value)}
                                onChange={handleWorkingSpaceSetupChange}
                            />

                            <label htmlFor={inputId}>{equipment.label}</label>
                        </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}

export default ApplicantJobDetails;